import { useEffect, useState } from 'react';
import { useSubmit } from 'react-router';
import { fullstoryEvent } from '@/fullstory/utils/fullstory-event';
import { getInsurancePlanNameFromTier } from '@/catalog/utils/get-insurance-plan-name-from-tier';

interface InsuranceTierOption {
  id: string;
  name: string;
}

interface FilterInsuranceTierProps {
  selectedInsuranceTier?: string;
  insurancePlanTierOptions?: string[];
  insurancePlanTierError?: string;
}

/**
 * Hook to manage the state of the Insurance Tier FilterSelectPill used in the Header.
 */
export function useFilterInsuranceTier({
  selectedInsuranceTier: selectedInsuranceTierFromSession,
  insurancePlanTierOptions,
  insurancePlanTierError,
}: FilterInsuranceTierProps) {
  const options =
    insurancePlanTierOptions?.map((tier) => ({
      id: tier,
      display_name: getInsurancePlanNameFromTier(tier),
    })) ?? [];
  const [selectedInsuranceTier, setSelectedInsuranceTier] = useState<InsuranceTierOption>(
    {
      id: selectedInsuranceTierFromSession ?? 'low',
      name: getInsurancePlanNameFromTier(selectedInsuranceTierFromSession ?? 'low'),
    }
  );

  const submit = useSubmit();

  const submitInsuranceTier = (insuranceTier: string) => {
    setSelectedInsuranceTier({
      id: insuranceTier,
      name: getInsurancePlanNameFromTier(insuranceTier),
    });
    fullstoryEvent('insurance_tier_changed', {
      insuranceTier,
    });
    submit(
      {
        intent: 'update_selected_insurance_tier',
        insurance_tier: insuranceTier,
      },
      { method: 'post' }
    );
  };

  // Reset selected insurance tier to whatever is in the cart if the action fails
  useEffect(() => {
    if (insurancePlanTierError) {
      setSelectedInsuranceTier({
        id: selectedInsuranceTierFromSession ?? 'low',
        name: getInsurancePlanNameFromTier(selectedInsuranceTierFromSession ?? 'low'),
      });
    }
  }, [insurancePlanTierError, selectedInsuranceTierFromSession]);

  return {
    selectedInsuranceTier,
    options,
    submitInsuranceTier,
  };
}
